import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Loader2, RotateCcw, Lock, Bell,
    FileText, AlertTriangle, Clock, AlertOctagon, Gauge,
    LockKeyhole, CreditCard, CreditCard as CreditCardAlert,
    Link2, Link2Off, Calendar, Megaphone,
    Trash2, ShieldCheck, Mail, CheckCircle2, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfirmModal from "./ConfirmModal";
import TelegramConnectCard from "./TelegramConnectCard";
import PushConnectCard from "./PushConnectCard";

/**
 * Notifications Matrix (PR #12).
 *
 * Backend contract — `/notification-prefs`:
 *   GET  → { prefs: {event: {channel: bool}}, events: [...meta], channels: [...] }
 *   PUT  → { prefs?: {...}, push_permission?, marketing_opt_in? }
 *          Server strips in_app=False on PUT (mandatory channel), EXCEPT
 *          for `product_news` where backend defaults in_app=False (opt-in marketing).
 *
 * UX:
 *   • Optimistic toggle + 600ms debounced PUT.
 *   • In-app is locked ON for all events (except product_news — promo opt-in).
 *   • Push & Telegram are visually present but disabled — "Скоро".
 *   • Email & In-app (where allowed) are interactive.
 *   • Reset → confirm modal → PUT with empty prefs → backend merge returns defaults.
 */

// Lucide icon mapping for backend `meta.icon` strings.
const ICON_MAP = {
    report:        FileText,
    anomaly:       AlertTriangle,
    clock:         Clock,
    alert:         AlertOctagon,
    gauge:         Gauge,
    lock:          LockKeyhole,
    card:          CreditCard,
    "card-alert":  CreditCardAlert,
    link:          Link2,
    unlink:        Link2Off,
    calendar:      Calendar,
    megaphone:     Megaphone,
    trash:         Trash2,
    "shield-check": ShieldCheck,
};

const SEVERITY_DOT = {
    info:     "bg-primary",
    warning:  "bg-warning",
    critical: "bg-error",
};

const SEVERITY_LABEL = {
    info:     "Інформ.",
    warning:  "Важливо",
    critical: "Критично",
};

const CHANNEL_META = {
    in_app:   { label: "In-app",   sub: "В дзвіночку",         locked: false, soon: false },
    email:    { label: "Email",    sub: "На пошту",            locked: false, soon: false },
    // push.locked is now dynamic — depends on browser subscription state.
    push:     { label: "Push",     sub: "У браузері",          locked: false, soon: false },
    // telegram.soon is false when bot is linked; we compute it dynamically below.
    telegram: { label: "Telegram", sub: "@AutoAds_AI_bot",     locked: false, soon: false },
};

const CHANNEL_ORDER = ["in_app", "email", "push", "telegram"];

const CheckboxCell = ({ checked, locked, soon, onChange, testid }) => {
    const interactive = !locked && !soon;
    const title = soon
        ? "Цей канал зʼявиться у наступних апдейтах"
        : locked
            ? "Завжди увімкнено — основа системи сповіщень"
            : checked ? "Увімкнено" : "Вимкнено";

    return (
        <button
            type="button"
            disabled={!interactive}
            onClick={interactive ? () => onChange(!checked) : undefined}
            title={title}
            aria-label={title}
            data-testid={testid}
            className={`relative w-9 h-9 rounded-md flex items-center justify-center transition-all
                        ${interactive
                            ? checked
                                ? "bg-primary/90 hover:bg-primary border border-primary cursor-pointer"
                                : "bg-surface-hover/30 hover:bg-surface-hover/60 border border-border cursor-pointer"
                            : checked
                                ? "bg-primary/30 border border-primary/40 cursor-not-allowed opacity-70"
                                : "bg-surface-hover/20 border border-border/60 cursor-not-allowed opacity-50"}`}
        >
            {checked && (
                <svg viewBox="0 0 16 16" className="w-4 h-4 text-white" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3.5 8.5L6.5 11.5L12.5 5" />
                </svg>
            )}
            {locked && !checked && (
                <Lock size={10} className="text-faint-c" />
            )}
        </button>
    );
};

const NotificationsSection = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [events, setEvents] = useState([]);     // catalog from backend
    const [prefs, setPrefs] = useState({});        // {event_type: {channel: bool}}
    const [confirmReset, setConfirmReset] = useState(false);
    const [testingEmail, setTestingEmail]   = useState(false);
    const [emailTestResult, setEmailTestResult] = useState(null); // null | {ok, message}
    const [showEmailDebug, setShowEmailDebug] = useState(false);
    const [emailDebug, setEmailDebug] = useState(null);
    const [loadingDebug, setLoadingDebug] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [tgConnected, setTgConnected] = useState(false);
    const [pushSubscribed, setPushSubscribed] = useState(false);
    const debounceRef = useRef(null);
    const inflightPrefs = useRef(null);  // last value we attempted to save (for rollback)

    // Initial load.
    useEffect(() => {
        (async () => {
            try {
                const [prefsRes, tgRes, pushRes] = await Promise.all([
                    api.get("/notification-prefs"),
                    api.get("/telegram/status").catch(() => null),
                    api.get("/push/status").catch(() => null),
                ]);
                setEvents(prefsRes.data?.events || []);
                setPrefs(prefsRes.data?.prefs || {});
                setTgConnected(!!tgRes?.data?.connected);
                setPushSubscribed(!!pushRes?.data?.subscribed);
            } catch (e) {
                toast.error("Не вдалося завантажити налаштування сповіщень");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Callback when TelegramConnectCard changes connection state.
    const handleTgStatusChange = async () => {
        try {
            const r = await api.get("/telegram/status");
            setTgConnected(!!r?.data?.connected);
        } catch {}
    };

    const handlePushStatusChange = async () => {
        try {
            const r = await api.get("/push/status");
            setPushSubscribed(!!r?.data?.subscribed);
        } catch {}
    };

    // Debounced PUT — saves whatever is in `prefs` 600ms after last toggle.
    const schedulePut = (next) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSaving(true);
            inflightPrefs.current = next;
            try {
                await api.put("/notification-prefs", { prefs: next });
                // No toast — saves are silent on individual toggles.
            } catch (e) {
                toast.error("Не вдалося зберегти. Повертаю попереднє значення.");
                // Rollback: re-fetch from backend to be safe.
                try {
                    const r = await api.get("/notification-prefs");
                    setPrefs(r.data?.prefs || {});
                } catch {}
            } finally {
                setSaving(false);
            }
        }, 600);
    };

    const handleToggle = (eventType, channel, nextVal) => {
        setPrefs((prev) => {
            const row = { ...(prev[eventType] || {}) };
            row[channel] = nextVal;
            const next = { ...prev, [eventType]: row };
            schedulePut(next);
            return next;
        });
    };

    /**
     * Returns column state for a channel across all events:
     *   'all'     — every (non-locked, non-required) row in this column is ON
     *   'none'    — every interactive row is OFF
     *   'partial' — mix of ON/OFF
     *   'locked'  — channel is not interactive at all (push not ready, telegram offline)
     */
    const getColumnState = (channel) => {
        if (channel === "push" && !pushSubscribed) return "locked";
        if (channel === "telegram" && !tgConnected) return "locked";

        let on = 0;
        let off = 0;
        for (const ev of events) {
            // In-app is force-ON for everything except `product_news` (promo).
            if (channel === "in_app" && ev.event_type !== "product_news") continue;
            const checked = !!prefs[ev.event_type]?.[channel];
            if (checked) on++;
            else off++;
        }
        if (on === 0) return "none";
        if (off === 0) return "all";
        return "partial";
    };

    /**
     * Bulk-toggle a channel across all events. 1-click on/off for the column.
     * Skips locked rows (in-app non-promo) so we never break the system invariant.
     */
    const handleBulkToggleColumn = (channel) => {
        const state = getColumnState(channel);
        if (state === "locked") return;
        // partial OR none → turn ALL on; all → turn ALL off.
        const nextVal = state !== "all";

        setPrefs((prev) => {
            const next = { ...prev };
            for (const ev of events) {
                // Don't touch in-app for required events.
                if (channel === "in_app" && ev.event_type !== "product_news") continue;
                next[ev.event_type] = {
                    ...(prev[ev.event_type] || {}),
                    [channel]: nextVal,
                };
            }
            schedulePut(next);
            return next;
        });

        const meta = CHANNEL_META[channel];
        toast.success(
            nextVal
                ? `${meta.label}: увімкнено для всіх подій`
                : `${meta.label}: вимкнено для всіх подій`
        );
    };

    const handleReset = async () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setResetting(true);
        try {
            // Empty prefs → backend returns defaults on next GET.
            await api.put("/notification-prefs", { prefs: {} });
            const r = await api.get("/notification-prefs");
            setPrefs(r.data?.prefs || {});
            toast.success("Повернуто до рекомендованих налаштувань");
            setConfirmReset(false);
        } catch (e) {
            toast.error("Не вдалося скинути налаштування");
        } finally {
            setResetting(false);
        }
    };

    // For product_news in_app is opt-in (off by default), for everything else — locked ON.
    const isInAppLocked = (eventType) => eventType !== "product_news";

    const hasCustomPrefs = useMemo(() => {
        // Naive check: if any event has at least one channel different from its default.
        for (const ev of events) {
            const cur = prefs[ev.event_type] || {};
            const def = ev.default_prefs || {};
            for (const ch of ["email", "push", "telegram"]) {
                if (cur[ch] !== undefined && cur[ch] !== def[ch]) return true;
            }
            if (ev.event_type === "product_news" && cur.in_app !== undefined && cur.in_app !== def.in_app) return true;
        }
        return false;
    }, [events, prefs]);

    const handleSendTestEmail = async () => {
        setTestingEmail(true);
        setEmailTestResult(null);
        try {
            const r = await api.post("/dev/email-test", { template: "notification_single" });
            const result = r?.data?.result || {};
            const configured = r?.data?.configured;
            if (result.status === "sent") {
                setEmailTestResult({
                    ok: true,
                    message: `Лист надіслано через Resend ✓ Перевірте пошту (resend_id: ${(result.resend_id || "").slice(0, 12)}…)`,
                });
                toast.success("Тестовий email надіслано — перевірте поштову скриньку");
            } else if (result.status === "stub" || !configured) {
                setEmailTestResult({
                    ok: false,
                    message: "Resend ще не підключений — RESEND_API_KEY відсутній на сервері. Лист збережено в outbox.",
                });
                toast.warning("Resend не налаштований — лист поки що в outbox");
            } else if (result.status === "skipped_no_email") {
                setEmailTestResult({ ok: false, message: "Немає email на вашому акаунті" });
                toast.error("Немає email на вашому акаунті");
            } else {
                setEmailTestResult({
                    ok: false,
                    message: `Не вдалося надіслати: ${result.reason || result.status || "невідома помилка"}`,
                });
                toast.error("Не вдалося надіслати тестовий email");
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Помилка мережі";
            setEmailTestResult({ ok: false, message: detail });
            toast.error(detail);
        } finally {
            setTestingEmail(false);
        }
    };

    const handleToggleDebug = async () => {
        if (showEmailDebug) {
            setShowEmailDebug(false);
            return;
        }
        setLoadingDebug(true);
        try {
            const r = await api.get("/dev/email-config");
            setEmailDebug(r.data);
            setShowEmailDebug(true);
        } catch (e) {
            toast.error("Не вдалося отримати діагностику: " + (e?.message || "помилка"));
        } finally {
            setLoadingDebug(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 py-8 text-sm text-faint-c" data-testid="notifications-loading">
                <Loader2 size={14} className="animate-spin" />
                Завантаження матриці…
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="notifications-section-content">
            {/* Header note */}
            <div className="text-xs text-secondary-c leading-relaxed">
                Оберіть які події і через які канали ви хочете отримувати.{" "}
                <span className="text-text-primary font-semibold">In-app</span> (дзвіночок) — основа системи.
                Інші канали (Email, Push, Telegram) потребують активації нижче.
            </div>

            {/* Telegram bot binding */}
            <TelegramConnectCard onChange={handleTgStatusChange} />

            {/* Browser push subscription */}
            <PushConnectCard onChange={handlePushStatusChange} />

            {/* Matrix */}
            <div className="rounded-xl border border-border overflow-x-auto"
                 data-testid="notifications-matrix">
                <table className="w-full text-sm min-w-[560px]">
                    <thead>
                        <tr className="border-b border-border bg-surface-hover/20">
                            <th className="text-left p-3 font-semibold text-text-primary">Подія</th>
                            {CHANNEL_ORDER.map((ch) => {
                                const meta = CHANNEL_META[ch];
                                const dim = meta.soon || (ch === "telegram" && !tgConnected) || (ch === "push" && !pushSubscribed);
                                const sub = (ch === "telegram" && !tgConnected)
                                    ? "Не підключено"
                                    : (ch === "push" && !pushSubscribed)
                                        ? "Не увімкнено"
                                        : (meta.soon ? "Скоро" : meta.sub);
                                const colState = getColumnState(ch);
                                const isLocked = colState === "locked";
                                const titleHint = isLocked
                                    ? "Колонка недоступна"
                                    : colState === "all"
                                        ? "Вимкнути все в цій колонці"
                                        : "Увімкнути все в цій колонці";
                                return (
                                    <th key={ch} className="p-3 text-center font-semibold w-[88px]">
                                        <button
                                            type="button"
                                            disabled={isLocked}
                                            onClick={() => handleBulkToggleColumn(ch)}
                                            title={titleHint}
                                            className={`w-full group ${isLocked ? "cursor-not-allowed" : "cursor-pointer hover:opacity-90"}`}
                                            data-testid={`notif-col-toggle-${ch}`}
                                        >
                                            <div className={`text-text-primary flex items-center justify-center gap-1.5 ${dim ? "opacity-50" : ""}`}>
                                                {!isLocked && (
                                                    <span
                                                        className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-[3px] border transition-colors ${
                                                            colState === "all"
                                                                ? "bg-primary border-primary"
                                                                : colState === "partial"
                                                                    ? "bg-primary/30 border-primary/60"
                                                                    : "bg-transparent border-border group-hover:border-primary/60"
                                                        }`}
                                                        aria-hidden
                                                    >
                                                        {colState === "all" && (
                                                            <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                                <path d="M2.5 6.5L5 9L9.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        )}
                                                        {colState === "partial" && (
                                                            <span className="block w-2 h-0.5 bg-white rounded" />
                                                        )}
                                                    </span>
                                                )}
                                                <span>{meta.label}</span>
                                            </div>
                                            <div className="text-[10px] font-normal text-faint-c mt-0.5">
                                                {sub}
                                            </div>
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((ev) => {
                            const Icon = ICON_MAP[ev.icon] || Bell;
                            const row = prefs[ev.event_type] || {};
                            const inAppLocked = isInAppLocked(ev.event_type);

                            return (
                                <tr key={ev.event_type}
                                    className="border-b border-border/40 last:border-b-0 hover:bg-surface-hover/10 transition-colors"
                                    data-testid={`notification-row-${ev.event_type}`}>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-md bg-surface-hover/40 flex items-center justify-center shrink-0">
                                                <Icon size={14} className="text-secondary-c" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-text-primary truncate">
                                                    {ev.label}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[ev.severity] || "bg-faint-c"}`} />
                                                    <span className="text-[10px] uppercase tracking-wider font-mono text-faint-c">
                                                        {SEVERITY_LABEL[ev.severity] || ev.severity}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {CHANNEL_ORDER.map((ch) => {
                                        const meta = CHANNEL_META[ch];
                                        const checked = !!row[ch];
                                        let locked = ch === "in_app" ? inAppLocked : meta.locked;
                                        let soon = meta.soon;
                                        // Telegram column locks until user binds the bot.
                                        if (ch === "telegram" && !tgConnected) {
                                            locked = true;
                                            soon = false;
                                        }
                                        // Push column locks until browser subscription is active.
                                        if (ch === "push" && !pushSubscribed) {
                                            locked = true;
                                            soon = false;
                                        }
                                        const hideChecked =
                                            (ch === "telegram" && !tgConnected) ||
                                            (ch === "push" && !pushSubscribed);
                                        return (
                                            <td key={ch} className="p-3 text-center">
                                                <div className="inline-flex">
                                                    <CheckboxCell
                                                        checked={checked && !hideChecked}
                                                        locked={locked}
                                                        soon={soon}
                                                        onChange={(v) => handleToggle(ev.event_type, ch, v)}
                                                        testid={`notif-toggle-${ev.event_type}-${ch}`}
                                                    />
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Footer: save indicator + reset */}
            <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
                <div className="text-[11px] text-faint-c h-5 flex items-center gap-1.5"
                     data-testid="notifications-save-status">
                    {saving && (
                        <>
                            <Loader2 size={11} className="animate-spin" />
                            <span>Зберігаю…</span>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleSendTestEmail}
                    disabled={testingEmail}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                               border border-border bg-surface-hover/30 hover:bg-surface-hover/60
                               text-text-primary text-xs font-semibold transition-colors
                               disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="send-test-email"
                    title="Надіслати тестовий лист на ваш email через Resend"
                >
                    {testingEmail ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <Mail size={12} />
                    )}
                    <span>{testingEmail ? "Надсилаю…" : "Тест email"}</span>
                </button>
                <button
                    type="button"
                    onClick={() => setConfirmReset(true)}
                    disabled={!hasCustomPrefs}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                               border border-border bg-surface-hover/30 hover:bg-surface-hover/60
                               text-text-primary text-xs font-semibold transition-colors
                               disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="notifications-reset"
                    title={hasCustomPrefs
                        ? "Повернути всі налаштування до рекомендованих"
                        : "Уже використовуються рекомендовані налаштування"}
                >
                    <RotateCcw size={12} />
                    <span>Скинути до рекомендованих</span>
                </button>
            </div>

            {emailTestResult && (
                <div
                    className={`flex items-start gap-2 px-3 py-2.5 rounded-md text-xs leading-relaxed
                                ${emailTestResult.ok
                                    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200"
                                    : "bg-amber-500/10 border border-amber-500/30 text-amber-200"}`}
                    data-testid="email-test-result"
                >
                    {emailTestResult.ok
                        ? <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
                        : <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />}
                    <span>{emailTestResult.message}</span>
                </div>
            )}
            <div className="space-y-2">
                <button
                    type="button"
                    onClick={handleToggleDebug}
                    disabled={loadingDebug}
                    className="inline-flex items-center gap-1.5 text-[11px] text-faint-c
                               hover:text-text-secondary transition-colors disabled:opacity-50"
                    data-testid="email-debug-toggle"
                >
                    {loadingDebug
                        ? <Loader2 size={11} className="animate-spin" />
                        : showEmailDebug
                            ? <ChevronUp size={11} />
                            : <ChevronDown size={11} />}
                    <span>{showEmailDebug ? "Сховати діагностику" : "Показати діагностику email"}</span>
                </button>

                {showEmailDebug && emailDebug && (
                    <div className="px-3 py-3 rounded-md bg-surface-hover/30 border border-border/60
                                    text-[11px] font-mono leading-relaxed text-text-secondary space-y-1"
                         data-testid="email-debug-panel">
                        <div className="grid grid-cols-[180px_1fr] gap-x-3 gap-y-1">
                            <span className="text-faint-c">is_configured:</span>
                            <span className={emailDebug.is_configured ? "text-emerald-300" : "text-amber-300"}>
                                {String(emailDebug.is_configured)}
                            </span>

                            <span className="text-faint-c">RESEND_API_KEY env:</span>
                            <span className={emailDebug.env_keys_seen?.RESEND_API_KEY ? "text-emerald-300" : "text-rose-300"}>
                                {emailDebug.env_keys_seen?.RESEND_API_KEY ? "присутній" : "ВІДСУТНІЙ"}
                            </span>

                            <span className="text-faint-c">key length:</span>
                            <span>{emailDebug.raw_key_length} символів</span>

                            <span className="text-faint-c">starts with 're_':</span>
                            <span className={emailDebug.raw_key_starts_re ? "text-emerald-300" : "text-rose-300"}>
                                {String(emailDebug.raw_key_starts_re)}
                            </span>

                            <span className="text-faint-c">passed re_ check:</span>
                            <span className={emailDebug.key_passed_check ? "text-emerald-300" : "text-rose-300"}>
                                {String(emailDebug.key_passed_check)}
                            </span>

                            <span className="text-faint-c">masked key:</span>
                            <span>{emailDebug.key_masked || "(порожньо)"}</span>

                            <span className="text-faint-c">dry_run:</span>
                            <span>{String(emailDebug.dry_run)}</span>

                            <span className="text-faint-c">from (noreply):</span>
                            <span className="break-all">{emailDebug.from_noreply}</span>

                            <span className="text-faint-c">from (hello):</span>
                            <span className="break-all">{emailDebug.from_hello}</span>

                            <span className="text-faint-c">EMAIL_FROM_NOREPLY:</span>
                            <span>{emailDebug.env_keys_seen?.EMAIL_FROM_NOREPLY ? "set" : "default"}</span>

                            <span className="text-faint-c">EMAIL_FROM_HELLO:</span>
                            <span>{emailDebug.env_keys_seen?.EMAIL_FROM_HELLO ? "set" : "default"}</span>

                            <span className="text-faint-c">resend SDK:</span>
                            <span className={emailDebug.resend_sdk?.installed ? "text-emerald-300" : "text-rose-300"}>
                                {emailDebug.resend_sdk?.installed
                                    ? `v${emailDebug.resend_sdk.version}${emailDebug.resend_sdk.has_async ? " (async ✓)" : " (sync only)"}`
                                    : `НЕ ВСТАНОВЛЕНИЙ — ${emailDebug.resend_sdk?.error || "unknown"}`}
                            </span>
                        </div>
                        {!emailDebug.resend_sdk?.installed && emailDebug.resend_sdk?.fix && (
                            <div className="mt-2 pt-2 border-t border-border/60 text-rose-300 text-[10.5px]">
                                💡 Fix: {emailDebug.resend_sdk.fix}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="text-[11px] text-faint-c leading-relaxed border-t border-border/60 pt-3">
                <span className="font-mono">ℹ️</span> Критичні події (платежі, втрата зв'язку з Google Ads)
                рекомендуємо тримати з email — щоб не пропустити навіть якщо вкладку закрито.
            </div>

            <ConfirmModal
                open={confirmReset}
                onClose={() => setConfirmReset(false)}
                onConfirm={handleReset}
                loading={resetting}
                title="Скинути всі налаштування?"
                body={
                    <>
                        Усі ваші зміни в матриці сповіщень будуть замінені{" "}
                        <strong>рекомендованими дефолтами</strong>.
                        Це не вимикає сповіщення — просто повертає до базових налаштувань.
                    </>
                }
                confirmLabel="Так, скинути"
                cancelLabel="Залишити"
                testidPrefix="notifications-reset-modal"
            />
        </div>
    );
};

export default NotificationsSection;